/**
 * DashboardStatusScreens — Loading, error, and calculating states
 *
 * Error states differentiate between Network, Data, and Auth errors
 * with distinct icons, messaging, CTA hierarchy, and auto-retry logic.
 */
import type { ReactNode } from "react";
import { AlertCircle, ShieldOff, WifiOff } from "lucide-react";
import { cn } from "../utils/cn";
import { useAutoRetry } from "../hooks/useAutoRetry";
import { DashboardSkeleton } from "./DashboardSkeleton";

/* ---------- helpers ---------- */

type ErrorType = "network" | "data" | "auth";

function detectErrorType(error: string): ErrorType {
  const lower = error.toLowerCase();
  if (
    lower.includes("network error") ||
    lower.includes("offline") ||
    lower.includes("failed to fetch") ||
    lower.includes("net::err_") ||
    lower.includes("timeout") ||
    lower.includes("connection refused") ||
    lower.includes("connection reset")
  ) {
    return "network";
  }
  return "data";
}

const ERROR_CONFIG = {
  network: {
    Icon: WifiOff,
    heading: "Connection Lost",
    description: "We can't reach our servers right now.",
    iconCircleClass: "error-state-icon-circle--network",
    iconClass: "error-state-icon--network",
  },
  data: {
    Icon: AlertCircle,
    heading: "Something Went Wrong",
    description: "We couldn't load your data.",
    iconCircleClass: "error-state-icon-circle--data",
    iconClass: "error-state-icon--data",
  },
  auth: {
    Icon: ShieldOff,
    heading: "Session Expired",
    description: "Please sign in again to continue.",
    iconCircleClass: "error-state-icon-circle--auth",
    iconClass: "error-state-icon--auth",
  },
} as const;

const SUPPORT_EMAIL = "mailto:support@retire-ez.ca";

/* ---------- shared layout ---------- */

interface StatusWrapperProps {
  children: ReactNode;
  id: string;
  className?: string;
}

function StatusWrapper({ children, id, className }: StatusWrapperProps) {
  return (
    <div className={cn("dashboard-status-container", className)} id={id}>
      <div className="full-screen-centered">{children}</div>
    </div>
  );
}

/* ---------- Loading ---------- */

export function DashboardLoading() {
  return <DashboardSkeleton />;
}

/* ---------- Profile Error (Auth) ---------- */

interface DashboardProfileErrorProps {
  error: string | null;
  onSignOut: () => void;
}

export function DashboardProfileError({
  error,
  onSignOut,
}: DashboardProfileErrorProps) {
  const config = ERROR_CONFIG.auth;
  const { Icon } = config;

  return (
    <div className="error-state-container" id="dashboard-profile-error">
      <div className={cn("error-state-icon-circle", config.iconCircleClass)}>
        <Icon className={cn("error-state-icon", config.iconClass)} />
      </div>
      <h2 className="error-state-heading">{config.heading}</h2>
      <p className="error-state-description">{config.description}</p>
      {error && <p className="error-state-detail-text">{error}</p>}
      <div className="error-state-actions">
        <button onClick={onSignOut} className="error-state-cta-secondary">
          Sign Out
        </button>
        <a href={SUPPORT_EMAIL} className="error-state-cta-tertiary">
          Contact Support
        </a>
      </div>
    </div>
  );
}

/* ---------- Projections Error (Network / Data) ---------- */

interface DashboardProjectionsErrorProps {
  error: string;
  onRetry: () => void;
}

export function DashboardProjectionsError({
  error,
  onRetry,
}: DashboardProjectionsErrorProps) {
  const showDetails = import.meta.env.VITE_SHOW_ERROR_DETAILS === "true";
  const errorType = detectErrorType(error);
  const config = ERROR_CONFIG[errorType];
  const { Icon } = config;

  const {
    countdown,
    countdownDuration,
    manualRetryOnly,
    isCountingDown,
    retry,
  } = useAutoRetry({ onRetry });

  const handleTryAgain = () => {
    retry();
  };

  return (
    <div className="error-state-container" id="dashboard-projections-error">
      <div className={cn("error-state-icon-circle", config.iconCircleClass)}>
        <Icon className={cn("error-state-icon", config.iconClass)} />
      </div>

      {manualRetryOnly ? (
        <>
          <h2 className="error-state-heading">
            We're having persistent trouble connecting.
          </h2>
        </>
      ) : (
        <>
          <h2 className="error-state-heading">{config.heading}</h2>
          <p className="error-state-description">{config.description}</p>
        </>
      )}

      {showDetails && <div className="error-state-details">{error}</div>}

      <div className="error-state-actions">
        <button onClick={handleTryAgain} className="error-state-cta-primary">
          {manualRetryOnly ? "Try Again Manually" : "Try Again"}
        </button>
        <a href={SUPPORT_EMAIL} className="error-state-cta-tertiary">
          Contact Support
        </a>
      </div>

      {isCountingDown && (
        <div className="error-retry-countdown">
          <div className="error-retry-text">Retrying in {countdown}s...</div>
          <div className="error-retry-bar-track">
            <div
              className="error-retry-bar-fill"
              style={{ animationDuration: `${countdownDuration}s` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Calculating ---------- */

export function DashboardCalculating() {
  return (
    <StatusWrapper id="dashboard-calculating">
      <div className="status-message">
        <img
          className="dashboard-calculating-illustration"
          src="/images/onboarding/dashboard-calculating.png"
          alt=""
          aria-hidden="true"
        />
        <div className="status-message-text">
          Calculating your projections...
        </div>
      </div>
    </StatusWrapper>
  );
}
